import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from "@/models/RootState.type"
import { AssessmentCalendar, IdentifiedPeriod, fetchCurrentPeriod as apiFetchCurrentPeriod, fetchAssessmentCalendar as apiFetchAssessmentCalendar, fetchSchoolYearCalendar as apiFetchSchoolYearCalendar, SchoolYearCalendar } from '@/services/query/calendarAPI';
import { getSchoolYear } from '@/components/Reports/util/validation';
import { Assignment } from '@/services/assignment/assignmentTypesAPI';
import { ASSIGNMENT_TYPE } from '@/services/assignment/constants';
import { getAssessmentWindowAssignments } from '@/services/assignment/getAssignment/getAssignment';

export interface CalendarState {
  currentPeriod: IdentifiedPeriod | null;
  assessmentCalendar: AssessmentCalendar | null;
  schoolYearCalendar: SchoolYearCalendar | null;
  loading: boolean;
  error: string | null;
  assessmentWindows: Record<string, Assignment[]> | null;
}

const initialState: CalendarState = {
  currentPeriod: null,
  assessmentCalendar: null,
  schoolYearCalendar: null,
  loading: false,
  error: null,
  assessmentWindows: null,
};

export const fetchCurrentPeriod = createAsyncThunk(
  'calendar/fetchCurrentPeriod',
  async (schoolId: string) => {
    try {
      const response = await apiFetchCurrentPeriod({ schoolId });
      if (!response) {
        throw new Error('Failed to fetch current period: No response received');
      }
      return response.getCurrentPeriod;
    } catch (error) {
      console.error('Error fetching current period:', error);
      throw error;
    }
  }
);

export const fetchAssessmentCalendar = createAsyncThunk(
  'calendar/fetchAssessmentCalendar',
  async ({schoolId}: {schoolId: string}, { getState }) => {
    try {
      // Get the current school year from the state or use a default
      const state = getState() as RootState;
      const {startDate} = getSchoolYear()
      const currentYear = new Date(startDate).getFullYear();

      const response = await apiFetchAssessmentCalendar({
        schoolId,
        schoolYear: currentYear
      });

      if (!response) {
        throw new Error('Failed to fetch assessment calendar: No response received');
      }
      return response.assessmentCalendar;
    } catch (error) {
      console.error('Error fetching assessment calendar:', error);
      throw error;
    }
  }
);

export const fetchSchoolYearCalendar = createAsyncThunk(
  'calendar/fetchSchoolYearCalendar',
  async ({districtId}: {districtId: string}, { getState }) => {
    try {
      // Get the current school year from the state or use a default
      const state = getState() as RootState;
      const {startDate} = getSchoolYear()
      const currentYear = new Date(startDate).getFullYear();

      const response = await apiFetchSchoolYearCalendar({
        districtId,
        schoolYear: currentYear
      });

      if (!response) {
        throw new Error('Failed to fetch school year calendar: No response received');
      }
      return response.schoolYearCalendar;
    } catch (error) {
      console.error('Error fetching school year calendar:', error);
      throw error;
    }
  }
);

export const fetchAssessmentWindows = createAsyncThunk(
  'calendar/fetchAssessmentWindows',
  async ({entityId, entityType, dateFrom, dateTo}: {entityId: string, entityType: string, dateFrom: string, dateTo: string}, { getState }) => {
    try {
      const districtAssignments = await getAssessmentWindowAssignments(
        entityId,
        entityType,
        dateFrom,
        dateTo,
      [ASSIGNMENT_TYPE.BENCHMARK, ASSIGNMENT_TYPE.PROGRESS_MONITORING]
    );

    const response = {
      assignment: [
        ...(districtAssignments?.assignment ?? []),
      ],
    };

    return response;
  } catch (error) {
    console.error("Error fetching assessment window assignments:", error);
    throw error;
    }
  }
);

const calendarSlice = createSlice({
  name: 'calendar',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchCurrentPeriod.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCurrentPeriod.fulfilled, (state, action) => {
        state.loading = false;
        state.currentPeriod = action.payload;
      })
      .addCase(fetchCurrentPeriod.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch current period';
      })
      .addCase(fetchAssessmentCalendar.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAssessmentCalendar.fulfilled, (state, action) => {
        state.loading = false;
        state.assessmentCalendar = action.payload;
      })
      .addCase(fetchAssessmentCalendar.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch assessment calendar';
      })
      .addCase(fetchSchoolYearCalendar.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSchoolYearCalendar.fulfilled, (state, action) => {
        state.loading = false;
        state.schoolYearCalendar = action.payload;
      })
      .addCase(fetchSchoolYearCalendar.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch school year calendar';
      })
      .addCase(fetchAssessmentWindows.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAssessmentWindows.fulfilled, (state, action) => {
        state.loading = false;
        state.assessmentWindows = action.payload;
      })
      .addCase(fetchAssessmentWindows.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch assessment windows';
      });
  },
});

// Selectors
export const selectCurrentPeriod = (state: RootState) => state.calendar.currentPeriod;
export const selectAssessmentCalendar = (state: RootState) => state.calendar.assessmentCalendar;
export const selectSchoolYearCalendar = (state: RootState) => state.calendar.schoolYearCalendar;
export const selectCalendarLoading = (state: RootState) => state.calendar.loading;
export const selectCalendarError = (state: RootState) => state.calendar.error;
export const selectAssessmentDistrictWindows = (state: RootState) => state.calendar.assessmentWindows;

export default calendarSlice.reducer;